import { useState, useEffect, useRef } from 'react';
import { useCopilotFileDownload, useCopilotStatus } from '@customerio/hydra-copilot/react';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { sentryCaptureException } from '@/utils/sentry';
import { extractFilename, fetchAsBlob } from '../helpers/file-utils';

interface UseCopilotImageThumbnailOptions {
  source: File | string;
  isImage: boolean;
  readOnly: boolean;
  session?: CopilotSession;
}

/**
 * Hook to handle image thumbnail generation for file chips.
 *
 * For readonly mode:
 * - Fetches signed URL and converts to blob for images
 * - Handles cleanup on unmount
 *
 * For interactive mode:
 * - Creates blob URL from File object
 * - Handles cleanup on unmount
 */
export function useCopilotImageThumbnail({ source, isImage, readOnly, session }: UseCopilotImageThumbnailOptions) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const status = useCopilotStatus(session ?? null);
  const { getFileDownloadUrl } = useCopilotFileDownload(session ?? null);

  useEffect(() => {
    if (!isImage || source == null || source === '') {
      setThumbnailUrl(null);
      return;
    }

    if (typeof source === 'string') {
      // Check if it's already a blob URL
      if (source.startsWith('blob:')) {
        setThumbnailUrl(source);
        return;
      }

      // If readonly mode and it's a filename or gs:// path, fetch the blob
      if (readOnly && session != null) {
        // Don't try to fetch if session isn't ready yet (prevents triggering session initialization)
        if (status === 'initializing' || status == null) {
          return;
        }

        const fetchImageBlob = async () => {
          try {
            // Extract filename from gs:// path if needed
            const filename = extractFilename(source);
            const signedUrl = await getFileDownloadUrl(filename);
            const blobUrl = await fetchAsBlob(signedUrl);
            blobUrlRef.current = blobUrl;
            setThumbnailUrl(blobUrl);
          } catch (error) {
            sentryCaptureException(error);
            // Set to null on error so it shows the icon fallback
            setThumbnailUrl(null);
          }
        };

        fetchImageBlob();
      }
    } else {
      // File object (for interactive mode with local files)
      const url = URL.createObjectURL(source);
      blobUrlRef.current = url;
      setThumbnailUrl(url);
    }

    // Cleanup blob URL when source changes or component unmounts
    return () => {
      if (blobUrlRef.current != null) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isImage, source, readOnly, getFileDownloadUrl, status]);

  return thumbnailUrl;
}
